
import { useState, useEffect } from 'react';
import { LeadScore, LeadScoreBreakdown } from '@/types/leadScore';
import { 
  calculateAndSaveLeadScore, 
  getScoreThresholds, 
  getLeadScoreCategory,
  getLeadScoreComplete
} from '@/services/leads/scoring';
import { useToast } from '@/components/ui/use-toast';

export const useLeadScore = (leadId: string) => {
  const [leadScore, setLeadScore] = useState<LeadScore | null>(null);
  const [scoreBreakdown, setScoreBreakdown] = useState<LeadScoreBreakdown | null>(null);
  const [scoreHistory, setScoreHistory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const { toast } = useToast();

  const fetchLeadScore = async () => {
    if (!leadId) return;
    
    setIsLoading(true);
    try {
      // Optimized: Fetch all data in parallel (2 queries instead of 3)
      const { score, breakdown, history } = await getLeadScoreComplete(leadId);
      
      setLeadScore(score);
      setScoreBreakdown(breakdown);
      setScoreHistory(history);
    } catch (error) {
      console.error('Error fetching lead score:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch lead score"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const recalculateScore = async (lead?: any, activities?: any[]) => {
    if (!leadId) return;
    
    setIsCalculating(true);
    try {
      // Pass pre-fetched data if available to avoid redundant API calls
      await calculateAndSaveLeadScore(
        lead ?? leadId,
        activities
      );
      
      // Re-fetch all data after recalculation using optimized function
      const { score, breakdown, history } = await getLeadScoreComplete(leadId);
      setLeadScore(score);
      setScoreBreakdown(breakdown);
      setScoreHistory(history);
      
      toast({
        title: "Score Recalculated",
        description: "Lead score has been updated successfully"
      });
    } catch (error) {
      console.error('Error recalculating lead score:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to recalculate lead score"
      });
    } finally {
      setIsCalculating(false);
    }
  };

  useEffect(() => {
    fetchLeadScore();
  }, [leadId]);

  return {
    leadScore,
    scoreBreakdown,
    scoreHistory,
    isLoading,
    isCalculating,
    recalculateScore,
    getScoreThresholds,
    getLeadScoreCategory
  };
};
